import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PDFDocument } from "pdf-lib";
import SignaturePad from "signature_pad";
import { listarUsuarios } from "../api";
import "./ContratoTab.css";

const API_BASE = "https://te-ayudo.es/back/api";

function isUserActive(user) {
  if (
    user?.is_active === undefined &&
    user?.activo === undefined &&
    user?.active === undefined
  ) {
    return true;
  }

  const value = user?.is_active ?? user?.activo ?? user?.active;

  return value === true || value === 1 || value === "1";
}

function isSelectableProfessional(user) {
  return isUserActive(user) && Boolean(user?.id && user?.nombre);
}

export default function ContratoTab({ paciente }) {
  const canvasRef = useRef(null);

  const [signaturePad, setSignaturePad] = useState(null);
  const [isSigned, setIsSigned] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [fileUrl, setFileUrl] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedProfessionalId, setSelectedProfessionalId] = useState("");
  const [professionals, setProfessionals] = useState([]);
  const [loadingProfessionals, setLoadingProfessionals] = useState(true);
  const [contractSpecialty, setContractSpecialty] = useState("general");

  const loadProfessionals = useCallback(async () => {
    try {
      setLoadingProfessionals(true);
      setError("");

      const usersResponse = await listarUsuarios({
        page: 1,
        page_size: 200,
      });

      const usersItems = Array.isArray(usersResponse)
        ? usersResponse
        : usersResponse?.items || [];

      const sortedProfessionals = usersItems
        .filter(isSelectableProfessional)
        .sort((a, b) =>
          String(a.nombre || "").localeCompare(
            String(b.nombre || ""),
            "es",
            { sensitivity: "base" },
          ),
        );

      setProfessionals(sortedProfessionals);
    } catch (err) {
      console.error("[ContratoTab] Error cargando profesionales:", err);
      setError(err.message || "No se pudieron cargar los profesionales.");
      setProfessionals([]);
    } finally {
      setLoadingProfessionals(false);
    }
  }, []);

  useEffect(() => {
    loadProfessionals();
  }, [loadProfessionals]);

  const selectedProfessional = useMemo(
    () =>
      professionals.find(
        (professional) =>
          String(professional.id) === String(selectedProfessionalId),
      ) || null,
    [professionals, selectedProfessionalId],
  );

  useEffect(() => {
    setFileUrl(null);
    setUploadedFile(null);
    setContractSpecialty("general");
    setMessage("");
    setError("");
  }, [selectedProfessionalId]);

  useEffect(() => {
    if (!paciente?.id || !selectedProfessionalId) {
      return undefined;
    }

    let cancelled = false;

    const generateContract = async () => {
      try {
        setLoading(true);
        setError("");
        setFileUrl(null);
        setUploadedFile(null);
        setMessage("");

        const params = new URLSearchParams({
          id: String(paciente.id),
          professional_id: String(selectedProfessionalId),
        });

        const url = `${API_BASE}/generate_contract.php?${params.toString()}`;

        const response = await fetch(url, {
          credentials: "include",
        });

        const text = await response.text();
        let data;

        try {
          data = text ? JSON.parse(text) : {};
        } catch {
          throw new Error("La respuesta del backend no es JSON válido.");
        }

        if (!response.ok) {
          throw new Error(data.error || `Error HTTP ${response.status}`);
        }

        if (!data.ok) {
          throw new Error(data.error || "Error al generar el contrato.");
        }

        const contractUrl = data.pdf_local || data.file_url;

        if (!contractUrl) {
          throw new Error("No se recibió ninguna URL del contrato.");
        }

        if (!cancelled) {
          setContractSpecialty(data.specialty_code || "general");
          setFileUrl(contractUrl);
        }
      } catch (err) {
        console.error("[ContratoTab] Error al obtener contrato:", err);

        if (!cancelled) {
          setError(err.message || "Error al obtener el contrato.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    generateContract();

    return () => {
      cancelled = true;
    };
  }, [paciente?.id, selectedProfessionalId]);

  useEffect(() => {
    if (!fileUrl || !canvasRef.current) {
      return undefined;
    }

    const canvas = canvasRef.current;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = 350;
    const height = 150;

    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const context = canvas.getContext("2d");
    context?.scale(ratio, ratio);

    const pad = new SignaturePad(canvas, {
      backgroundColor: "#ffffff",
      penColor: "#000000",
    });

    const handleEndStroke = () => {
      setIsSigned(!pad.isEmpty());
    };

    if (typeof pad.addEventListener === "function") {
      pad.addEventListener("endStroke", handleEndStroke);
    } else {
      pad.onEnd = handleEndStroke;
    }

    setSignaturePad(pad);
    setIsSigned(false);

    return () => {
      if (typeof pad.removeEventListener === "function") {
        pad.removeEventListener("endStroke", handleEndStroke);
      }

      pad.off();
      pad.clear();
      setSignaturePad(null);
      setIsSigned(false);
    };
  }, [fileUrl]);

  const handleClear = () => {
    signaturePad?.clear();
    setIsSigned(false);
  };

  const handleSave = async () => {
    if (!selectedProfessionalId) {
      alert("Selecciona al profesional responsable.");
      return;
    }

    if (!signaturePad || signaturePad.isEmpty()) {
      alert("Por favor, firma el contrato antes de guardar.");
      return;
    }

    if (!fileUrl) {
      alert("No se ha podido cargar el contrato para firmar.");
      return;
    }

    setUploading(true);
    setMessage("");
    setError("");

    try {
      const pdfResponse = await fetch(fileUrl);

      if (!pdfResponse.ok) {
        throw new Error("No se pudo descargar el PDF original.");
      }

      const existingPdfBytes = await pdfResponse.arrayBuffer();
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const pages = pdfDoc.getPages();

      if (!pages.length) {
        throw new Error("El PDF no contiene páginas.");
      }

      const lastPage = pages[pages.length - 1];
      const { width } = lastPage.getSize();

      const signatureDataUrl = signaturePad.toDataURL("image/png");
      const signatureResponse = await fetch(signatureDataUrl);
      const pngBytes = await signatureResponse.arrayBuffer();
      const pngImage = await pdfDoc.embedPng(pngBytes);

      lastPage.drawImage(pngImage, {
        x: width / 2 + 20,
        y: 75,
        width: 185,
        height: 70,
      });

      const pdfBytes = await pdfDoc.save();
      const pdfBlob = new Blob([pdfBytes], {
        type: "application/pdf",
      });

      const safeSpecialty = contractSpecialty || "general";
      const signedFilename =
        `contrato_firmado_${paciente.id}_${selectedProfessionalId}.pdf`;

      const formData = new FormData();
      formData.append("file", pdfBlob, signedFilename);
      formData.append("patient_id", String(paciente.id));
      formData.append("type", "contrato");
      formData.append("specialty", safeSpecialty);
      formData.append("professional_id", String(selectedProfessionalId));

      const uploadResponse = await fetch(`${API_BASE}/upload_drive.php`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      const uploadText = await uploadResponse.text();
      let uploadData;

      try {
        uploadData = uploadText ? JSON.parse(uploadText) : {};
      } catch {
        throw new Error("La respuesta de subida no es JSON válido.");
      }

      if (!uploadResponse.ok || !uploadData.ok) {
        throw new Error(uploadData.error || "Error al subir el contrato.");
      }

      setUploadedFile({
        webViewLink: uploadData.file?.webViewLink || null,
        pdf_local: uploadData.pdf_local || null,
      });

      setMessage("✅ Contrato firmado y subido correctamente.");

      const mailForm = new FormData();
      mailForm.append("patient_id", String(paciente.id));
      mailForm.append("specialty", safeSpecialty);
      mailForm.append("professional_id", String(selectedProfessionalId));
      mailForm.append("file", pdfBlob, signedFilename);

      const mailResponse = await fetch(
        `${API_BASE}/send_contract_mail.php`,
        {
          method: "POST",
          body: mailForm,
          credentials: "include",
        },
      );

      const mailText = await mailResponse.text();
      let mailData;

      try {
        mailData = mailText ? JSON.parse(mailText) : {};
      } catch {
        throw new Error(
          "El contrato se ha subido, pero la respuesta del correo no es válida.",
        );
      }

      if (mailResponse.ok && mailData.ok) {
        setMessage(
          "✅ Contrato firmado, subido y enviado por correo correctamente.",
        );
      } else {
        setMessage(
          `⚠️ Contrato subido, pero no se pudo enviar por correo: ${
            mailData.error || "error desconocido"
          }`,
        );
      }
    } catch (err) {
      console.error("[ContratoTab] Error guardando contrato:", err);
      setMessage(`❌ ${err.message || "Error al guardar el contrato."}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="contrato-tab">
      <h3>🧾 Contrato de prestación de servicios</h3>

      <div className="form-row">
        <label htmlFor="contract-professional">
          Profesional responsable
        </label>

        <select
          id="contract-professional"
          value={selectedProfessionalId}
          onChange={(event) =>
            setSelectedProfessionalId(event.target.value)
          }
          disabled={loadingProfessionals}
        >
          <option value="">
            {loadingProfessionals
              ? "Cargando profesionales..."
              : "-- Selecciona un profesional --"}
          </option>

          {professionals.map((professional) => (
            <option key={professional.id} value={professional.id}>
              {professional.nombre}
            </option>
          ))}
        </select>

        {selectedProfessional && (
          <p className="form-help">
            Profesional seleccionado: {" "}
            <strong>{selectedProfessional.nombre}</strong>
          </p>
        )}
      </div>

      {loading && <p>Generando contrato personalizado...</p>}

      {error && <p style={{ color: "red" }}>❌ {error}</p>}

      {!loading && fileUrl && (
        <>
          <div className="contrato-preview-wrapper">
            <iframe
              src={fileUrl}
              title="Vista previa contrato PDF"
              className="contrato-preview"
            />
          </div>

          <p className="contrato-open-fullscreen-row">
            <a
              href={fileUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-open-fullscreen"
            >
              📄 Abrir contrato a pantalla completa
            </a>
          </p>
        </>
      )}

      {fileUrl && (
        <div className="signature-section">
          <h4>✍️ Firma del paciente o tutor</h4>

          <div className="signature-box">
            <canvas ref={canvasRef} className="signature-canvas" />
          </div>

          <div className="signature-actions">
            <button
              type="button"
              onClick={handleClear}
              disabled={uploading || !isSigned}
            >
              Borrar firma
            </button>

            <button
              type="button"
              onClick={handleSave}
              className="primary-btn"
              disabled={
                uploading ||
                !isSigned ||
                !selectedProfessionalId
              }
            >
              {uploading ? "Guardando..." : "Guardar contrato firmado"}
            </button>
          </div>
        </div>
      )}

      {message && (
        <div className="message-box">
          <p>{message}</p>

          {uploadedFile && (
            <button
              type="button"
              className="btn-ver-contrato"
              onClick={() => {
                const url =
                  uploadedFile.pdf_local || uploadedFile.webViewLink;

                if (url) {
                  window.open(url, "_blank", "noopener,noreferrer");
                }
              }}
            >
              📄 Ver contrato firmado
            </button>
          )}
        </div>
      )}
    </div>
  );
}